import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { fetchOrderDetails as fetchOrderDetailsService, addPartialPayment } from '../services/ordersService'
import { fetchOrderDetails } from '../features/orders/ordersSlice'
import { AppDispatch, RootState } from '../app/store'
import { Order } from '../types'
import { isCustomerServiceUser } from '../utils/apiUtils'
import { useCountryConfig } from './useCountryConfig'
import { getCurrencyTextLabel } from '../utils/currencyUtils'

const AVAILABLE_AMOUNT_TOAST_ID = 'partial-payment-available-amount-toast'

export interface PaymentHistory {
  id: string | number
  amount: number
  paymentMethod: 'cash' | 'network'
  status: 'success' | 'failed'
  transactionId: string
}

interface UsePartialPaymentReturn {
  // State
  order: Order | null
  isLoading: boolean
  error: string | null
  paymentAmount: string
  selectedPaymentMethod: 'cash' | 'network' | null
  isSubmitting: boolean
  paymentHistory: PaymentHistory[]
  remainingAmount: number
  totalAmount: number
  isLastPayment: boolean
  currentPaymentCount: number
  
  // Handlers
  setPaymentAmount: (amount: string) => void
  setSelectedPaymentMethod: (method: 'cash' | 'network' | null) => void
  handleNumberPress: (number: string) => void
  handleDeletePress: () => void
  handleSelectPaymentMethod: (method: 'cash' | 'network') => void
  handleSubmitPayment: (method: 'cash' | 'network', transactionId?: string) => Promise<void>
  refreshOrderDetails: () => Promise<void>
}

export function usePartialPayment(): UsePartialPaymentReturn {
  const { t, i18n } = useTranslation()
  const countryConfig = useCountryConfig()
  const currencyLabel = getCurrencyTextLabel(
    countryConfig,
    t('currency.egyptSymbol'),
    t('common.currency')
  )
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { settings } = useSelector((state: RootState) => state.settings)
  const isRTL = settings.language === 'ar'
  const textDirection = (isRTL ? 'rtl' : 'ltr') as 'rtl' | 'ltr'
  // Keep the available-amount warning toast unique and short-lived.
  const availableAmountToastOptions = useMemo(() => ({
    id: AVAILABLE_AMOUNT_TOAST_ID,
    duration: 2500,
    style: {
      background: '#363636',
      color: '#fff',
      fontSize: '16px',
      fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
      direction: textDirection,
    },
  }), [textDirection, isRTL])
  
  // Ensure i18n language matches settings
  useEffect(() => {
    if (i18n.language !== settings.language) {
      i18n.changeLanguage(settings.language)
    }
  }, [settings.language, i18n])

  const [order, setOrder] = useState<Order | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [paymentAmount, setPaymentAmount] = useState('')
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'cash' | 'network' | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([])

  // Load order details
  const loadOrderDetails = useCallback(async () => {
    if (!orderId) {
      setError(t('payment.order_id_missing'))
      setIsLoading(false)
      return null
    }

    setIsLoading(true)
    setError(null)
    try {
      const orderDetails = await fetchOrderDetailsService(orderId)
      setOrder(orderDetails)
      
      // Load payment history from order data - use partial_payments/partialPayments (matching Flutter)
      // Try multiple possible keys to extract partial payments
      const payments = (orderDetails as any).partial_payments || 
                      (orderDetails as any).partialPayments || 
                      (orderDetails as any).payments ||
                      []
      
      // Always process payments array, even if empty
      if (Array.isArray(payments)) {
        if (payments.length > 0) {
          const history: PaymentHistory[] = payments.map((payment: any) => ({
            id: payment.id || 0,
            amount: typeof payment.amount === 'string' 
              ? parseFloat(payment.amount || '0') 
              : (typeof payment.amount === 'number' ? payment.amount : parseFloat(payment.paid_amount || '0')),
            paymentMethod: payment.payment_method === 'network' || 
                          payment.payment_method === 'card' || 
                          payment.payment_method === 'online' ? 'network' : 'cash',
            status: payment.status === 'success' || 
                   payment.is_success === 1 || 
                   payment.isSuccess === 1 || 
                   payment.is_successful === true ||
                   payment.isSuccessful === true ? 'success' : 'failed',
            transactionId: payment.transaction_id || 
                          payment.transactionId || 
                          payment.transaction_number || 
                          payment.id?.toString() || ''
          }))
          setPaymentHistory(history)
        } else {
          // Empty array - no payments yet
          setPaymentHistory([])
        }
      } else {
        // Not an array - set empty
        setPaymentHistory([])
      }
      
      return orderDetails
    } catch (err: any) {
      const errorMsg = err?.message || t('payment.error_loading_order')
      setError(errorMsg)
      toast.error(errorMsg, {
        duration: 5000,
        style: {
          background: '#363636',
          color: '#fff',
          fontSize: '16px',
          fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
          direction: isRTL ? 'rtl' : 'ltr',
        },
      })
      return null
    } finally {
      setIsLoading(false)
    }
  }, [orderId, t, isRTL])

  // Initial load
  useEffect(() => {
    loadOrderDetails()
  }, [loadOrderDetails])

  // Calculate remaining amount - use API remainingPaymentAmount (matching Flutter)
  const totalAmount = order?.total_amount || 0
  const apiRemainingAmount = (order as any)?.remainingPaymentAmount || 
                            (order as any)?.remaining_payment_amount || 
                            (order as any)?.remaining_amount
  
  // If API provides remainingPaymentAmount, use it; otherwise calculate manually
  const remainingAmount = apiRemainingAmount !== undefined 
    ? apiRemainingAmount 
    : (totalAmount - paymentHistory.reduce((sum, payment) => sum + payment.amount, 0))

  // Check if this is the last payment (third payment) - match Flutter logic exactly
  // Flutter: currentPaymentCount >= 2 means this will be the third payment
  const currentPaymentCount = paymentHistory.length
  const isLastPayment = currentPaymentCount >= 2 && remainingAmount > 0

  // Auto-set amount for last payment (matching Flutter behavior)
  useEffect(() => {
    if (isLastPayment && !paymentAmount && remainingAmount > 0) {
      setPaymentAmount(remainingAmount.toFixed(2))
    } else if (!isLastPayment && paymentAmount && currentPaymentCount < 2) {
      // Clear amount for first/second payments if it was auto-set
      // This handles the case when payment count changes
    }
  }, [isLastPayment, remainingAmount, currentPaymentCount])

  // Handle numeric keyboard input
  const handleNumberPress = useCallback((number: string) => {
    // Note: Payment method check should be done in the component that calls this function
    // This allows the component to open the payment method modal instead of showing an error
    // We still check here as a safety measure, but don't show error to allow component to handle it
    if (!selectedPaymentMethod) {
      return
    }
    
    if (isLastPayment) {
      // For third payment, force the remaining amount (matching Flutter)
      setPaymentAmount(remainingAmount.toFixed(2))
      return
    }

    const newAmount = paymentAmount + number
    const numValue = parseFloat(newAmount)
    
    // Don't allow amount greater than remaining
    if (numValue <= remainingAmount) {
      setPaymentAmount(newAmount)
    } else {
      // Auto-set to remaining amount if exceeds
      setPaymentAmount(remainingAmount.toFixed(2))
      toast.error(
        t('payment.available_amount', { amount: remainingAmount.toFixed(2), currency: currencyLabel }),
        availableAmountToastOptions
      )
    }
  }, [paymentAmount, remainingAmount, isLastPayment, selectedPaymentMethod, t, availableAmountToastOptions])

  const handleDeletePress = useCallback(() => {
    if (isLastPayment) {
      // Don't allow editing last payment amount (matching Flutter)
      return
    }
    setPaymentAmount(prev => prev.slice(0, -1))
  }, [isLastPayment])

  // Handle payment method selection
  const handleSelectPaymentMethod = useCallback((method: 'cash' | 'network') => {
    if (method === 'network') {
      setSelectedPaymentMethod('network')
    } else if (method === 'cash') {
      setSelectedPaymentMethod('cash')
    }
  }, [])

  // Handle payment submission
  const handleSubmitPayment = useCallback(async (method: 'cash' | 'network', transactionId?: string) => {
    const toastStyle = {
      duration: 5000,
      style: {
        background: '#363636',
        color: '#fff',
        fontSize: '16px',
        fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
        direction: isRTL ? 'rtl' : 'ltr',
      } as const,
    }

    if (!orderId) {
      toast.error(t('payment.order_id_missing'), toastStyle)
      return
    }
    
    // Use method parameter if provided, otherwise use selectedPaymentMethod from state
    // If method parameter is provided, use it even if state is null (e.g., from Wosool payment)
    const paymentMethod = method || selectedPaymentMethod
    
    if (!paymentMethod) {
      toast.error(t('payment.please_select_payment_method'), toastStyle)
      return
    }

    // Check if payment method is network - require payment machine connection for non-CS users
    if (paymentMethod === 'network' && !isCustomerServiceUser()) {
      toast.error(t('payment.connect_payment_machine'), toastStyle)
      return
    }

    const amount = parseFloat(paymentAmount)
    if (isNaN(amount) || amount <= 0) {
      toast.error(t('payment.please_enter_valid_amount'), toastStyle)
      return
    }

    if (amount > remainingAmount) {
      toast.error(
        t('payment.available_amount', { amount: remainingAmount.toFixed(2), currency: currencyLabel }),
        availableAmountToastOptions
      )
      return
    }

    // Validate third payment - must equal remaining amount exactly (matching Flutter)
    // Use a small epsilon for floating point comparison to handle precision issues
    const epsilon = 0.001
    if (isLastPayment && Math.abs(amount - remainingAmount) > epsilon) {
      toast.error(t('payment.last_payment_full_amount', { amount: remainingAmount.toFixed(2) }), toastStyle)
      return
    }

    setIsSubmitting(true)
    try {
      const result = await addPartialPayment(orderId, {
        amount: amount,
        paymentMethod: paymentMethod === 'network' ? 'network' : 'cash',
        isSuccess: 1,
        transactionId: transactionId
      })

      toast.success(t('payment.payment_added_successfully'), {
        duration: 3000,
        style: {
          background: '#4CAF50',
          color: '#fff',
          fontSize: '16px',
          fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
          direction: isRTL ? 'rtl' : 'ltr',
        },
      })
      
      // Reload order details to get updated payment history
      const updatedOrderDetails = await loadOrderDetails()

      // Reset form
      setPaymentAmount('')
      setSelectedPaymentMethod(null)
      
      // Check if fully paid - use the updated order data
      if (updatedOrderDetails) {
        const newRemaining = (updatedOrderDetails as any)?.remainingPaymentAmount ?? 
                            (updatedOrderDetails.total_amount - ((updatedOrderDetails as any)?.partial_payments || (updatedOrderDetails as any)?.partialPayments || []).reduce((sum: number, p: any) => sum + ((p.is_success === 1 || p.isSuccess === 1 || p.is_successful === true || p.isSuccessful === true) ? (typeof p.amount === 'number' ? p.amount : parseFloat(p.amount || '0')) : 0), 0))
        
        // Consider order fully paid only if remaining is exactly 0.00 (with small epsilon for floating point precision)
        // Use small epsilon (0.001) to handle floating point precision, but not allow 0.01
        const epsilon = 0.001
        if (newRemaining <= epsilon) {
          // Order is fully paid - navigate to order confirmation page with auto-print
          try {
            // Fetch order details using Redux to ensure state is updated
            const detailsResult = await dispatch(fetchOrderDetails(orderId!) as any)
            
            if (fetchOrderDetails.fulfilled.match(detailsResult)) {
              // Navigate to confirmation page with order details and auto-print flag
              setTimeout(() => {
                navigate('/order-confirmation', {
                  state: { 
                    orderId: orderId!,
                    orderData: detailsResult.payload || updatedOrderDetails,
                    shouldAutoPrint: true
                  },
                  replace: false
                })
              }, 1500)
            } else {
              // Even if fetching details fails, navigate with the orderId we have
              setTimeout(() => {
                navigate('/order-confirmation', {
                  state: { 
                    orderId: orderId!,
                    orderData: updatedOrderDetails,
                    shouldAutoPrint: true
                  },
                  replace: false
                })
              }, 1500)
            }
          } catch (fetchError) {
            // If fetching details fails, still navigate with what we have
            setTimeout(() => {
              navigate('/order-confirmation', {
                state: { 
                  orderId: orderId!,
                  orderData: updatedOrderDetails,
                  shouldAutoPrint: true
                },
                replace: false
              })
            }, 1500)
          }
        }
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || t('payment.error_adding_payment')
      toast.error(errorMsg, {
        duration: 5000,
        style: {
          background: '#363636',
          color: '#fff',
          fontSize: '16px',
          fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
          direction: isRTL ? 'rtl' : 'ltr',
        },
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [
    orderId,
    selectedPaymentMethod,
    paymentAmount,
    remainingAmount,
    isLastPayment,
    loadOrderDetails,
    order,
    totalAmount,
    paymentHistory,
    navigate,
    dispatch,
    t,
    isRTL,
    availableAmountToastOptions,
  ])

  // Wrapper for refreshOrderDetails that returns Promise<void>
  const refreshOrderDetails = useCallback(async () => {
    await loadOrderDetails()
  }, [loadOrderDetails])

  return {
    // State
    order,
    isLoading,
    error,
    paymentAmount,
    selectedPaymentMethod,
    isSubmitting,
    paymentHistory,
    remainingAmount,
    totalAmount,
    isLastPayment,
    currentPaymentCount,
    
    // Handlers
    setPaymentAmount,
    setSelectedPaymentMethod,
    handleNumberPress,
    handleDeletePress,
    handleSelectPaymentMethod,
    handleSubmitPayment,
    refreshOrderDetails,
  }
}

